<template>
  <v-container fluid class="relative header-space container">
    <v-row class="mb-4">
      <v-col>
        <h1>Dubletten-Kandidat prüfen</h1>
        <v-btn text small :to="{ path: '/konflikte', query: { tab: 'dubletten' } }">
          <v-icon left>mdi-arrow-left</v-icon>Zurück zu Konflikten
        </v-btn>
      </v-col>
    </v-row>

    <div v-if="loading" class="text-center pa-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div v-else-if="candidate">
      <v-row class="mb-2">
        <v-col>
          <v-chip class="mr-2" :color="statusColor(candidate.status)" dark>{{ candidate.status }}</v-chip>
          <span class="caption">Pass: <b>{{ candidate.detectionPass }}</b></span>
          &nbsp;·&nbsp;
          <span class="caption">Ähnlichkeit: <b>{{ candidate.similarity }}%</b></span>
          &nbsp;·&nbsp;
          <span class="caption">Typ: <b>{{ candidate.entityType }}</b></span>
        </v-col>
      </v-row>

      <v-row>
        <!-- Source entity -->
        <v-col cols="12" md="6">
          <v-card outlined>
            <v-card-title class="subtitle-1">
              <v-icon left color="orange">mdi-import</v-icon>
              Neu importiert (Quelle)
            </v-card-title>
            <v-card-text>
              <pre class="entity-json">{{ JSON.stringify(sourceEntity, null, 2) }}</pre>
            </v-card-text>
            <v-card-actions>
              <v-btn text small tag="a" :href="'/' + candidate.entityType + '/' + candidate.sourceEntityId" target="_blank" rel="noopener noreferrer">
                <v-icon left>mdi-open-in-new</v-icon>Öffnen
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <!-- Target entity -->
        <v-col cols="12" md="6">
          <v-card outlined>
            <v-card-title class="subtitle-1">
              <v-icon left color="blue">mdi-database</v-icon>
              Vorhanden (Ziel)
            </v-card-title>
            <v-card-text>
              <pre class="entity-json">{{ JSON.stringify(targetEntity, null, 2) }}</pre>
            </v-card-text>
            <v-card-actions>
              <v-btn text small tag="a" :href="'/' + candidate.entityType + '/' + candidate.targetEntityId" target="_blank" rel="noopener noreferrer">
                <v-icon left>mdi-open-in-new</v-icon>Öffnen
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Action buttons (only for pending candidates) -->
      <v-row v-if="candidate.status === 'pending'" class="mt-4">
        <v-col class="text-center">
          <v-btn large class="mr-4 success" :loading="actionLoading" v-on:click="keep">
            <v-icon left>mdi-check</v-icon>Behalten (kein Duplikat)
          </v-btn>
          <v-btn large class="error" :loading="actionLoading" v-on:click="merge">
            <v-icon left>mdi-merge</v-icon>Zusammenführen
          </v-btn>
        </v-col>
      </v-row>

      <!-- Already resolved -->
      <v-row v-else class="mt-4">
        <v-col class="text-center grey--text">
          Gelöst am {{ new Date(candidate.resolvedAt).toLocaleString() }} von {{ candidate.resolvedBy }}.
        </v-col>
      </v-row>

      <!-- Feedback snackbar -->
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" top>
        {{ snackbar.message }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" v-on:click="snackbar.show = false">Schließen</v-btn>
        </template>
      </v-snackbar>
    </div>

    <div v-else class="text-center pa-8 grey--text">
      Kandidat nicht gefunden.
    </div>
  </v-container>
</template>

<script>
export default {
  props: ['id'],
  data: () => ({
    candidate: null,
    sourceEntity: null,
    targetEntity: null,
    loading: true,
    actionLoading: false,
    snackbar: { show: false, message: '', color: 'success' }
  }),
  methods: {
    statusColor (status) {
      if (status === 'pending') return 'orange'
      if (status === 'merged') return 'error'
      return 'success'
    },
    async keep () {
      this.actionLoading = true
      try {
        const response = await this.$http.post(`/duplicate-candidate/${this.id}/keep`)
        this.candidate = response.data
        this.showSnackbar('Als "behalten" markiert.', 'success')
      } catch (e) {
        this.showSnackbar('Fehler: ' + (e.response?.data?.error || e.message), 'error')
      } finally {
        this.actionLoading = false
      }
    },
    async merge () {
      this.actionLoading = true
      try {
        const response = await this.$http.post(`/duplicate-candidate/${this.id}/merge`)
        this.candidate = response.data.candidate
        this.showSnackbar('Entitäten zusammengeführt.', 'success')
      } catch (e) {
        this.showSnackbar('Fehler: ' + (e.response?.data?.error || e.message), 'error')
      } finally {
        this.actionLoading = false
      }
    },
    showSnackbar (message, color) {
      this.snackbar = { show: true, message, color }
    }
  },
  async created () {
    await this.$auth.waitForAuth()
    if (!this.$auth.isAuthenticated) {
      this.$router.push('/')
      return
    }
    try {
      const response = await this.$http.get(`/duplicate-candidate/${this.id}`)
      this.candidate = response.data

      // Load both entities for side-by-side comparison
      const [srcRes, tgtRes] = await Promise.all([
        this.$http.get(`/${this.candidate.entityType}/${this.candidate.sourceEntityId}`).catch(() => null),
        this.$http.get(`/${this.candidate.entityType}/${this.candidate.targetEntityId}`).catch(() => null)
      ])
      this.sourceEntity = srcRes?.data ?? null
      this.targetEntity = tgtRes?.data ?? null
    } catch (e) {
      this.candidate = null
    } finally {
      this.loading = false
    }
  }
}
</script>

<style scoped>
.entity-json {
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 500px;
  overflow-y: auto;
}
</style>
